<script lang="ts">
  import {createEventDispatcher} from 'svelte';

  import type {Week} from 'calendarize';

  const dispatch = createEventDispatcher();

  export let type: 'date' | 'month';
  export let selectedDate: Date | null;
  export let minDate: Date | null;
  export let maxDate: Date | null;
  export let year: number;
  export let month: number;
  export let weekdays: Array<string>;
  export let weekdayOffset: number; // Offset 1 to start the week on Monday
  export let previousMonth: Array<Week>;
  export let currentMonth: Array<Week>;
  export let nextMonth: Array<Week>;

  $: selectedMonth = selectedDate && selectedDate.getMonth();
  $: selectedYear = selectedDate && selectedDate.getFullYear();
  $: selectedDay = selectedDate && selectedDate.getDate();

  // Helper function to get the date for the given day and month weekdayOffset
  const getDateForDayAndMonthOffset = (
    day: number,
    monthOffset: number = 0
  ) => {
    let newMonth = month + monthOffset;
    let newYear = year;

    // Handle turn of the year
    if (newMonth < 0) {
      newMonth = 11;
      newYear -= 1;
    } else if (newMonth > 11) {
      newMonth = 0;
      newYear += 1;
    }

    return new Date(newYear, newMonth, day);
  };

  // Handle select the clicked date
  const selectDate = (day: number, monthOffset?: number) => {
    const date = getDateForDayAndMonthOffset(day, monthOffset);

    if (type === 'month') {
      date.setDate(1);
      date.setHours(0, 0, 0, 0);
    }

    selectedDate = date;
    dispatch('change', selectedDate);
  };

  // Returns whether the given day is today or not
  const isToday = (day: number, monthOffset?: number) => {
    const today = new Date();
    const thisYear = today.getFullYear();
    const thisMonth = today.getMonth();
    const thisDay = today.getDate();

    const date = getDateForDayAndMonthOffset(day, monthOffset);
    const givenYear = date.getFullYear();
    const givenMonth = date.getMonth();

    return (
      thisYear === givenYear && thisMonth === givenMonth && thisDay === day
    );
  };

  // Returns whether the given day is selected or not
  $: isSelected = (day: number, monthOffset?: number) => {
    const date = getDateForDayAndMonthOffset(day, monthOffset);
    const givenYear = date.getFullYear();
    const givenMonth = date.getMonth();

    return (
      selectedDate &&
      selectedYear === givenYear &&
      selectedMonth === givenMonth &&
      (type === 'month' || selectedDay === day)
    );
  };

  // Returns whether the given day is disabled or not
  $: isDisabled = (day: number, monthOffset?: number) => {
    const date = getDateForDayAndMonthOffset(day, monthOffset);
    const min = minDate;
    const max = maxDate;

    // Set minimum date to first of month and maximum date to last of month
    // in month mode
    if (type === 'month') {
      if (min) {
        min.setDate(1);
        min.setHours(0, 0, 0, 0);
      }
      if (max) {
        max.setMonth(max.getMonth() + 1);
        max.setDate(1);
        max.setHours(0, 0, 0, -1);
      }
    }

    return (min && date < min) || (max && date > max);
  };
</script>

<style lang="postcss">
  .daysContainer {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
  }

  .weekday {
    display: flex;
    align-items: center;
    justify-content: center;
    height: var(--ubilabs-calendar-tile-size);
    font-weight: 500;
  }

  .daysContainer > button {
    height: var(--ubilabs-calendar-tile-size);
    border-radius: var(--ubilabs-calendar-tile-border-radius);
    color: var(--ubilabs-calendar-tile-color);
  }

  .daysContainer > button.neighboringMonth {
    color: var(--ubilabs-calendar-tile-neighboring-month-color);
  }

  .daysContainer > button.today {
    color: var(--ubilabs-calendar-tile-now-color);
  }

  .daysContainer > button.selected,
  .daysContainer > button.selected:not([disabled]):hover,
  .daysContainer > button.selected:not([disabled]):focus-visible {
    background-color: var(--ubilabs-calendar-tile-selected-background-color);
    color: var(--ubilabs-calendar-tile-active-color);
  }
</style>

<div class="daysContainer">
  {#each weekdays as weekday, weekdayIndex (weekday)}
    <div class="weekday">
      {weekdays[(weekdayIndex + weekdayOffset) % 7]}
    </div>
  {/each}

  {#each {length: 6} as _, weekIndex (weekIndex)}
    {#if currentMonth[weekIndex]}
      {#each {length: 7} as _, dayIndex (dayIndex)}
        {#if currentMonth[weekIndex][dayIndex] !== 0}
          <button
            class:today={isToday(currentMonth[weekIndex][dayIndex])}
            class:selected={isSelected(currentMonth[weekIndex][dayIndex])}
            disabled={isDisabled(currentMonth[weekIndex][dayIndex])}
            on:click={() => {
              if (!isDisabled(currentMonth[weekIndex][dayIndex])) {
                selectDate(currentMonth[weekIndex][dayIndex]);
              }
            }}>
            {currentMonth[weekIndex][dayIndex]}
          </button>
        {:else if weekIndex < 1}
          <button
            class="neighboringMonth"
            class:today={isToday(
              previousMonth[previousMonth.length - 1][dayIndex],
              -1
            )}
            class:selected={isSelected(
              previousMonth[previousMonth.length - 1][dayIndex],
              -1
            )}
            disabled={isDisabled(
              previousMonth[previousMonth.length - 1][dayIndex],
              -1
            )}
            on:click={() => {
              if (
                !isDisabled(
                  previousMonth[previousMonth.length - 1][dayIndex],
                  -1
                )
              ) {
                selectDate(
                  previousMonth[previousMonth.length - 1][dayIndex],
                  -1
                );
              }
            }}>
            {previousMonth[previousMonth.length - 1][dayIndex]}
          </button>
        {:else}
          <button
            class="neighboringMonth"
            class:today={isToday(nextMonth[0][dayIndex], 1)}
            class:selected={isSelected(nextMonth[0][dayIndex], 1)}
            disabled={isDisabled(nextMonth[0][dayIndex], 1)}
            on:click={() => {
              if (!isDisabled(nextMonth[0][dayIndex], 1)) {
                selectDate(nextMonth[0][dayIndex], 1);
              }
            }}>
            {nextMonth[0][dayIndex]}
          </button>
        {/if}
      {/each}
    {/if}
  {/each}
</div>
